import { AlertTriangle, Clock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useSubscriptionContext } from '@/app/providers/subscription-provider'
import { useCreatePortal, useReactivateSubscription } from '@/hooks/use-subscription'

/**
 * Global billing banner — renders at most ONE banner based on priority:
 *
 * 1. isPaused          -> red  "Hesap Durduruldu"
 * 2. isGraceExpired    -> skip (RequirePro guard shows blocker)
 * 3. isGracePeriod     -> amber/red "Odeme Basarisiz"
 * 4. isGiftExpired     -> amber "Sure Doldu"
 * 5. isTrialExpired    -> amber "Deneme Suresi Doldu"
 * 6. isCancelPending   -> amber "Iptal Bekliyor"
 * 7. trialDaysRemaining <= 3 -> amber "Deneme Suresi Bitiyor"
 */
export function BillingBanner() {
  const {
    subscription,
    isPaused,
    isGracePeriod,
    isGraceExpired,
    isGiftExpired,
    isTrialExpired,
    isCancelPending,
    gracePeriodDaysRemaining,
    trialDaysRemaining,
    paymentIssue,
    isLoading,
  } = useSubscriptionContext()

  const navigate = useNavigate()
  const portalMutation = useCreatePortal()
  const reactivateMutation = useReactivateSubscription()

  // Don't show banner while loading or if subscription is null (admin users, etc.)
  if (isLoading || !subscription) return null

  // ------- Priority 1: Account paused -------
  if (isPaused) {
    return (
      <BannerShell color="red" icon={<AlertTriangle className="h-4 w-4 shrink-0" />}>
        <BannerBody
          badgeVariant="destructive"
          badgeText="Hesap Durduruldu"
          message="Hesabınız durduruldu. Pro özellikleri geçici olarak kullanılamıyor."
        />
      </BannerShell>
    )
  }

  // ------- Priority 2: Grace expired -> skip (RequirePro handles blocker) -------
  if (isGraceExpired) return null

  // ------- Priority 3: Grace period (past_due + grace active) -------
  // paymentIssue doluyken aynı mesaj yan menü kartında gösterilir (#981); burada tekrarlanmaz.
  if (isGracePeriod && !paymentIssue) {
    const isUrgent = (gracePeriodDaysRemaining ?? 0) <= 1
    const color = isUrgent ? 'red' : 'amber'
    return (
      <BannerShell color={color} icon={<AlertTriangle className="h-4 w-4 shrink-0" />}>
        <BannerBody
          badgeVariant={isUrgent ? 'destructive' : 'secondary'}
          badgeClassName={isUrgent ? undefined : 'border-none bg-amber-500 text-white'}
          badgeText="Ödeme Başarısız"
          message={`Son ödemeniz başarısız oldu. ${gracePeriodDaysRemaining} gün içinde ödeme yönteminizi güncellemezseniz erişiniz kısıtlanacak.`}
        />
        <Button
          size="sm"
          variant="outline"
          className={`shrink-0 ${color === 'red' ? 'border-red-500/40 text-red-400 hover:bg-red-500/10' : 'border-amber-500/40 text-amber-400 hover:bg-amber-500/10'}`}
          onClick={() => portalMutation.mutate()}
          disabled={portalMutation.isPending}
        >
          {portalMutation.isPending ? 'Yükleniyor...' : 'Ödeme Güncelle'}
        </Button>
      </BannerShell>
    )
  }

  // ------- Priority 4: Gift expired -------
  if (isGiftExpired) {
    return (
      <BannerShell color="amber" icon={<Clock className="h-4 w-4 shrink-0" />}>
        <BannerBody
          badgeVariant="secondary"
          badgeClassName="border-none bg-amber-500 text-white"
          badgeText="Süre Doldu"
          message="Hediye aboneliğinizin süresi doldu. Pro'ya geçerek tüm özelliklere erişmeye devam edin."
        />
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
          onClick={() => navigate('/pricing')}
        >
          Pro'ya Geç
        </Button>
      </BannerShell>
    )
  }

  // ------- Priority 5: Trial expired -------
  if (isTrialExpired) {
    return (
      <BannerShell color="amber" icon={<Clock className="h-4 w-4 shrink-0" />}>
        <BannerBody
          badgeVariant="secondary"
          badgeClassName="border-none bg-amber-500 text-white"
          badgeText="Deneme Süresi Doldu"
          message="Deneme süreniz sona erdi. Pro'ya geçerek tüm özelliklere erişmeye devam edin."
        />
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
          onClick={() => navigate('/pricing')}
        >
          Pro'ya Geç
        </Button>
      </BannerShell>
    )
  }

  // ------- Priority 6: Cancel pending -------
  if (isCancelPending) {
    const endDate = subscription.cancel_at ?? subscription.current_period_end
    const formattedDate = endDate
      ? new Date(endDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
      : 'dönem sonunda'
    return (
      <BannerShell color="amber" icon={<Clock className="h-4 w-4 shrink-0" />}>
        <BannerBody
          badgeVariant="secondary"
          badgeClassName="border-none bg-amber-500 text-white"
          badgeText="İptal Bekliyor"
          message={`Aboneliğiniz ${formattedDate} tarihinde sona erecek. Erişiniz bu tarihe kadar devam eder.`}
        />
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
          onClick={() => reactivateMutation.mutate()}
          disabled={reactivateMutation.isPending}
        >
          {reactivateMutation.isPending ? 'Yükleniyor...' : 'Yeniden Aktifleştir'}
        </Button>
      </BannerShell>
    )
  }

  // ------- Priority 7: Trial ending soon (<=3 days) -------
  if (trialDaysRemaining !== null && trialDaysRemaining <= 3) {
    return (
      <BannerShell color="amber" icon={<Clock className="h-4 w-4 shrink-0" />}>
        <BannerBody
          badgeVariant="secondary"
          badgeClassName="border-none bg-amber-500 text-white"
          badgeText="Deneme Süresi Bitiyor"
          message={
            trialDaysRemaining === 0
              ? 'Deneme süreniz bugün doluyor. Pro\'ya geçerek erişimi koruyun.'
              : `${trialDaysRemaining} gün içinde deneme süreniz dolacak. Pro'ya geçerek erişimi koruyun.`
          }
        />
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
          onClick={() => navigate('/pricing')}
        >
          Pro'ya Geç
        </Button>
      </BannerShell>
    )
  }

  return null
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BannerShell({
  color,
  icon,
  children,
}: {
  color: 'red' | 'amber'
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  const borderColor = color === 'red' ? 'border-red-500/25' : 'border-amber-500/25'
  const bgColor = color === 'red' ? 'bg-red-500/10' : 'bg-amber-500/10'
  const textColor = color === 'red' ? 'text-red-400' : 'text-amber-400'

  return (
    <div className="flex flex-col gap-4 px-6 pt-4 lg:px-10">
      <div className={`flex items-start justify-between gap-4 rounded-xl border ${borderColor} ${bgColor} px-4 py-3 text-sm ${textColor} shadow-sm`}>
        {icon}
        {children}
      </div>
    </div>
  )
}

function BannerBody({
  badgeVariant,
  badgeClassName,
  badgeText,
  message,
}: {
  badgeVariant: 'destructive' | 'secondary'
  badgeClassName?: string
  badgeText: string
  message: string
}) {
  const color = badgeVariant === 'destructive' ? 'text-red-400/80' : 'text-amber-400/80'
  return (
    <div className="flex flex-1 flex-col gap-1">
      <Badge
        variant={badgeVariant}
        lang="tr"
        className={badgeClassName ?? 'w-max text-xs font-semibold uppercase tracking-wide'}
      >
        {badgeText}
      </Badge>
      <p className={color}>{message}</p>
    </div>
  )
}
